const ENTRY_MAX_AGE_MS = 30_000;

function toBase64(input: string): string {
  return btoa(unescape(encodeURIComponent(input)));
}

function fromBase64(input: string): string {
  return decodeURIComponent(escape(atob(input)));
}

function toBase64Url(input: string): string {
  return toBase64(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(input: string): string {
  const padLength = (4 - (input.length % 4)) % 4;
  const padded = `${input}${'='.repeat(padLength)}`.replace(/-/g, '+').replace(/_/g, '/');
  return fromBase64(padded);
}

function xorText(text: string, key: string): string {
  if (!key) {
    return text;
  }
  const output: string[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const charCode = text.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    output.push(String.fromCharCode(charCode));
  }
  return output.join('');
}

export function encryptEntryPayload(payload: Record<string, string>, timestamp: number): string {
  const sourceText = JSON.stringify(payload);
  const encrypted = xorText(sourceText, String(timestamp));
  return toBase64Url(encrypted);
}

export function decryptEntryPayload(
  encryptedPayload: string,
  timestamp: number
): Record<string, string> | null {
  try {
    const raw = fromBase64Url(encryptedPayload);
    const decrypted = xorText(raw, String(timestamp));
    return JSON.parse(decrypted) as Record<string, string>;
  } catch {
    return null;
  }
}

export function isEntryTimestampValid(timestamp: number): boolean {
  if (!Number.isFinite(timestamp)) {
    return false;
  }
  return Math.abs(Date.now() - timestamp) <= ENTRY_MAX_AGE_MS;
}

/** Persisted shape for sessionStorage (not subject to ENTRY_MAX_AGE_MS on read). */
type EncryptedEnvelopeV1 = { v: 1; ts: number; enc: string };

export function sealEncryptedParams(params: Record<string, string>): string {
  const ts = Date.now();
  const enc = encryptEntryPayload(params, ts);
  const envelope: EncryptedEnvelopeV1 = { v: 1, ts, enc };
  return JSON.stringify(envelope);
}

export function openEncryptedParams(raw: string): Record<string, string> | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const o = parsed as Record<string, unknown>;
    if (o.v !== 1 || typeof o.enc !== 'string') {
      return null;
    }
    const ts = Number(o.ts);
    if (!Number.isFinite(ts)) {
      return null;
    }
    return decryptEntryPayload(o.enc, ts);
  } catch {
    return null;
  }
}
